<template>
  <div
    :class="bem.b('container')"
    :style="{ width: addUnit(width), height: addUnit(height) }"
    v-bind="containerAttrs"
  >
    <!-- 步骤条 -->
    <div
      :class="[bem.b('nav'), bem.is(miniSize ? 'mini' : '')]"
      :style="{ padding: stepPadding }"
    >
      <steps
        v-bind="componentAttrs"
        v-model:current="currentVal"
        :progress-dot="type === 'breadcrumb' ? false : progressDot"
        size="default"
        :items="type === 'inline' ? items : undefined"
        :class="[
          bem.b(),
          bem.is(type === 'tabs' ? 'tabs' : ''),
          bem.is(type === 'breadcrumb' ? 'breadcrumb' : ''),
          bem.is(
            left && (type === 'navigation' || type === 'breadcrumb')
              ? 'left'
              : '',
          ),
        ]"
        :type="type === 'tabs' || type === 'breadcrumb' ? 'navigation' : type"
        :direction="type === 'default' ? direction : 'horizontal'"
      >
        <template v-if="type !== 'inline'">
          <template v-for="(item, index) in items" :key="index">
            <a-step v-bind="filterItem(item)">
              <template v-if="!progressDot || type === 'breadcrumb'" #icon>
                <slot name="icon" v-bind="{ item, index }">
                  <ll-icon
                    v-if="item.icon"
                    :icon-name="item.icon"
                    color="inherit"
                  />
                  <span v-else>{{ index + 1 }}</span>
                </slot>
              </template>

              <!-- 子标题 type='breadcrumb' 不显示子标题  -->
              <template
                v-if="type !== 'breadcrumb' || $slots.subTitle"
                #subTitle
              >
                <slot name="subTitle" v-bind="{ item, index }">
                  <span v-if="item.subTitle">{{ item.subTitle }}</span>
                </slot>
              </template>

              <!-- 描述 type='breadcrumb' 不显示描述 -->
              <template
                v-if="type !== 'breadcrumb' || $slots.description"
                #description
              >
                <slot name="description" v-bind="{ item, index }">
                  <span v-if="item.description">{{ item.description }}</span>
                </slot>
              </template>

              <!-- 其余插槽 -->
              <template
                v-for="slot in Object.keys($slots).filter(
                  (i) =>
                    i !== 'icon' && i !== 'description' && i !== 'subTitle',
                )"
                :key="slot"
                #[slot]
              >
                <slot :name="slot" v-bind="{ item, index }"></slot>
              </template>
            </a-step>
          </template>
        </template>
      </steps>
    </div>
    <!-- 内容 -->
    <div
      v-if="stepItems.length > 0 && direction !== 'vertical' && content"
      :class="bem.e('content')"
    >
      <component :is="currentStepComponent" v-if="currentStepComponent" />
    </div>

    <!-- 页脚 -->
    <div
      v-if="footer && direction !== 'vertical'"
      :class="[bem.e('footer'), bem.is(getFooterPositional)]"
    >
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { addUnit, createNamespace } from '@ll-plus/utils'
import { Steps } from 'ant-design-vue'
import { omit } from 'lodash-es'
import { computed, ref, useAttrs, useSlots } from 'vue'
import { stepsProps } from './steps'

defineOptions({ name: 'LlSteps', inheritAttrs: false })
const bem = createNamespace('steps')
const attrs = useAttrs()

// 容器样式
const containerAttrs = computed(() => ({
  class: attrs.class as any,
  style: attrs.style as any,
}))

// 组件功能相关的属性
const componentAttrs = computed(() => omit(attrs, ['class', 'style']))

const props = defineProps(stepsProps)
const emits = defineEmits(['update:current'])

const internalCurrent = ref(0)

const currentVal = computed({
  get: () => props.current || internalCurrent.value,
  set: (v: number) => {
    if (props.clicked) {
      internalCurrent.value = v
      emits('update:current', v)
    }
  },
})

const slots = useSlots()

// 计算当前步骤的组件
const currentStepComponent = computed(() => {
  const currentStep = stepItems.value.find(
    (step) => step.key === currentVal.value,
  )
  return currentStep?.component || null
})

// 获取所有子组件（StepItem）
const stepItems = computed(() => {
  const children = slots.default?.() || []
  const result = [] as any
  let index = 0

  const processNode = (node) => {
    if (!node) return

    // 1. 检查是否是 LlStepItem
    if (
      node.type &&
      typeof node.type === 'object' &&
      node.type.name === 'LlStepItem'
    ) {
      result.push({
        index: index,
        key: index,
        component: node,
      })
      index++ // 处理完一个组件后索引加1
    }
    // 2. 检查是否是 Fragment
    else if (node.type?.toString() === 'Symbol(v-fgt)') {
      if (node.children) {
        node.children.forEach(processNode)
      }
    }
  }

  children.forEach(processNode)
  return result
})

const filterItem = (item) => {
  // 获取 slots 的所有键名
  const slotKeys = new Set([
    ...Object.keys(slots),
    'icon',
    'description',
    'subTitle',
  ])

  // 从 item 中移除与 slots 键名匹配的属性
  const newItem = Object.fromEntries(
    Object.entries(item).filter(([key]) => !slotKeys.has(key)),
  )

  return newItem
}

// 页脚位置
const getFooterPositional = computed(() => {
  switch (props.footerPositional) {
    case 'left':
      return 'left'
    case 'center':
      return 'center'
    case 'right':
      return 'right'
    default:
      return 'right'
  }
})
</script>
